import { useRef } from 'react'
import { PlusIcon, XIcon } from '@phosphor-icons/react'
import AddTagShadcn from 'app/components/tags/AddTagShadcn'
import Tag, { setColorCache } from 'app/components/tags/Tag'

/** Tags applied to contacts, so colours resolve against the contact tag model. */
const TAG_MODEL = `contacts`

type TagPickerProps = {
  value: string[]
  onChange: (tags: string[]) => void
}

/** A selected tag rendered the same way it appears on a contact (coloured pill), with a remove
 *  button that works for any tag — including built-ins, which the pill itself won't remove. */
function TagChip({ tag, onRemove }: { tag: string; onRemove: () => void }) {
  return (
    <span className="inline-flex items-center gap-0.5">
      <Tag tag={tag} model={TAG_MODEL} readOnly removeTag={onRemove} />
      <button
        type="button"
        onClick={onRemove}
        aria-label={`Remove ${tag}`}
        className="flex size-4 items-center justify-center rounded text-minimal hover:bg-surface-muted hover:text-default"
      >
        <XIcon size={10} weight="bold" />
      </button>
    </span>
  )
}

/** Reuses the app's tag UI: the AddTag popover (search existing tags or create a new one via the
 *  colour-picker modal), with the current selection shown as contact-style coloured pills. */
export function TagPicker({ value, onChange }: TagPickerProps) {
  const containerRef = useRef<HTMLDivElement>(null)

  const addTag = (tag: string, color?: string) => {
    if (color) setColorCache(tag, TAG_MODEL, color)
    if (!value.includes(tag)) onChange([...value, tag])
  }
  const removeTag = (tag: string) => onChange(value.filter((t) => t !== tag))

  return (
    <div ref={containerRef}>
      {value.length > 0 && (
        <div className="mb-2 flex flex-wrap items-center gap-1.5">
          {value.map((tag) => (
            <TagChip key={tag} tag={tag} onRemove={() => removeTag(tag)} />
          ))}
        </div>
      )}
      <AddTagShadcn
        containerRef={containerRef}
        shouldConfirmNewTag={false}
        passColorOnSelect
        onAdd={addTag}
        customButton={
          <button
            type="button"
            className="flex w-full items-center gap-1.5 rounded-lg border border-dashed border-default px-3 py-2 text-left text-xs font-medium text-subtle hover:border-strong hover:text-default"
          >
            <PlusIcon size={13} weight="bold" />
            Add tag
          </button>
        }
      />
    </div>
  )
}
